import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

const ChangePassword: React.FC = () => {
  const colors = getThemeColors('dark');

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validate current password
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Please enter your current password';
    }

    // Validate new password
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'Please enter a new password';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Validate confirm password
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is different from current
    if (formData.currentPassword && formData.newPassword && formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert('Password changed successfully!');

      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

    } catch (error) {
      console.error('Failed to change password:', error);
      alert('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '600px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    formCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    inputGroup: {
      marginBottom: spacing.lg,
    },
    inputLabel: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    inputContainer: {
      position: 'relative',
    },
    input: {
      width: '100%',
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
    },
    inputError: {
      borderColor: colors.error,
    },
    passwordToggle: {
      position: 'absolute',
      right: spacing.md,
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      color: colors.textSecondary,
      fontSize: 18,
    },
    errorText: {
      fontSize: typography.fontSize.sm,
      color: colors.error,
      marginTop: spacing.sm,
    },
    passwordRequirements: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginTop: spacing.sm,
    },
    requirementsTitle: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    requirementItem: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
      display: 'flex',
      alignItems: 'center',
    },
    requirementIcon: {
      marginRight: spacing.sm,
      fontSize: 12,
    },
    submitButton: {
      width: '100%',
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      cursor: 'pointer',
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    securityTips: {
      backgroundColor: colors.warning + '10',
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      border: `1px solid ${colors.warning}`,
      marginTop: spacing.lg,
    },
    securityTipsTitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.warning,
      marginBottom: spacing.sm,
    },
    securityTip: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
  };

  const passwordRequirements = [
    { text: 'At least 8 characters long', met: formData.newPassword.length >= 8 },
    { text: 'Contains at least one uppercase letter', met: /[A-Z]/.test(formData.newPassword) },
    { text: 'Contains at least one lowercase letter', met: /[a-z]/.test(formData.newPassword) },
    { text: 'Contains at least one number', met: /\d/.test(formData.newPassword) },
  ];

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Change Password"
        subtitle="Update your account password"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>🔐</div>
            <h1 style={styles.headerTitle}>Change Password</h1>
            <p style={styles.headerSubtitle}>Update your account password for better security</p>
          </div>

          {/* Password Change Form */}
          <div style={styles.formCard}>
            <h2 style={styles.sectionTitle}>Password Details</h2>

            {/* Current Password */}
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Current Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  style={{
                    ...styles.input,
                    ...(errors.currentPassword ? styles.inputError : {}),
                  }}
                  placeholder="Enter your current password"
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                />
                <span
                  style={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPasswords.current ? '🙈' : '👁️'}
                </span>
              </div>
              {errors.currentPassword && (
                <div style={styles.errorText}>{errors.currentPassword}</div>
              )}
            </div>

            {/* New Password */}
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>New Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  style={{
                    ...styles.input,
                    ...(errors.newPassword ? styles.inputError : {}),
                  }}
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                />
                <span
                  style={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? '🙈' : '👁️'}
                </span>
              </div>
              {errors.newPassword && (
                <div style={styles.errorText}>{errors.newPassword}</div>
              )}

              {/* Password Requirements */}
              {formData.newPassword && (
                <div style={styles.passwordRequirements}>
                  <div style={styles.requirementsTitle}>Password Requirements:</div>
                  {passwordRequirements.map((req, index) => (
                    <div key={index} style={styles.requirementItem}>
                      <span style={styles.requirementIcon}>
                        {req.met ? '✅' : '❌'}
                      </span>
                      {req.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Confirm New Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  style={{
                    ...styles.input,
                    ...(errors.confirmPassword ? styles.inputError : {}),
                  }}
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                />
                <span
                  style={styles.passwordToggle}
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? '🙈' : '👁️'}
                </span>
              </div>
              {errors.confirmPassword && (
                <div style={styles.errorText}>{errors.confirmPassword}</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              style={{
                ...styles.submitButton,
                ...(loading ? styles.submitButtonDisabled : {}),
              }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>

          {/* Security Tips */}
          <div style={styles.securityTips}>
            <div style={styles.securityTipsTitle}>🔒 Security Tips</div>
            <div style={styles.securityTip}>• Use a strong, unique password</div>
            <div style={styles.securityTip}>• Don't reuse passwords from other accounts</div>
            <div style={styles.securityTip}>• Enable two-factor authentication for extra security</div>
            <div style={styles.securityTip}>• Change your password regularly</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;